/* Mira's compact purchase UI. Shopify, not this script, applies the automatic discount. */
(() => {
  'use strict';
  if (customElements.get('mira-bundle-builder')) return;
  const discounts = new Map([[3, 200], [6, 600], [12, 1900]]);
  const totals = new Map([[1, 900], [3, 2500], [6, 4800], [12, 8900]]);
  class MiraBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.starting) return;
      this.starting = true;
      this.startTimer = setTimeout(() => { this.starting = false; if (this.isConnected) this.initialize(); }, 0);
    }
    initialize() {
      if (this.ready) return;
      try {
        this.rows = [...this.querySelectorAll('[data-soap-id]')];
        if (!this.rows.length || this.dataset.currency !== 'CAD') return;
        this.byId = new Map(this.rows.map(r => [r.dataset.soapId, r]));
        this.counts = new Map(this.rows.map(r => [r.dataset.soapId, 0]));
        this.currentId = this.dataset.currentId;
        if (!this.byId.has(this.currentId)) return;
        this.size = 1; this.mode = 'same'; this.busy = false; this.uncertain = false;
        this.abort = new AbortController();
        this.money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });
        this.root = window.Shopify?.routes?.root || '/';
        this.cartUrl = this.dataset.cartUrl || this.root + 'cart';
        this.form = document.getElementById('shopify-section-' + this.dataset.sectionId)?.querySelector('.buy-buttons-block');
        this.nativeWidgets = new Map();
        this.addEventListener('change', e => this.change(e), {signal: this.abort.signal});
        this.addEventListener('click', e => this.click(e), {signal: this.abort.signal});
        document.addEventListener('bundler:bundle_widget_created', () => this.hideNative(), {signal: this.abort.signal});
        this.observer = new MutationObserver(() => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {this.frame = null; this.hideNative();});
        });
        this.observer.observe(document.getElementById('MainContent') || document.body, {childList:true, subtree:true});
        this.ready = true;
        this.update(); this.hidden = false; this.hideNative();
        if (location.hash === '#mira-bundle-builder') {
          this.size = 3;
          this.querySelector('[data-size][value="3"]').checked = true;
          this.fillSame(); this.update();
          requestAnimationFrame(() => this.scrollIntoView({block:'start'}));
        }
      } catch (error) {
        this.restore(); this.ready = false; this.hidden = true;
        console.warn('Mira bundle controls could not start. Standard controls remain available.', error);
      }
    }
    disconnectedCallback() { clearTimeout(this.startTimer); this.starting = false; this.restore(); this.ready = false; }
    restore() {
      this.abort?.abort(); this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.form?.classList.remove('mira-native-purchase-hidden');
      for (const node of this.nativeWidgets?.keys() || []) node.removeAttribute('data-mira-native-hidden');
    }
    hideNative() {
      if (!this.ready || !this.isConnected) return;
      for (const node of document.querySelectorAll('.bndlr-container')) {
        if (node.hasAttribute('data-mira-native-hidden')) continue;
        if (node.querySelector('.bndlr-product') && /build your bundle\s*&\s*save/i.test(node.textContent)) {
          this.nativeWidgets.set(node, true); node.setAttribute('data-mira-native-hidden', 'true');
        }
      }
    }
    cap(id) { return Math.max(0, Number(this.byId.get(id)?.dataset.cap || 0)); }
    totalCount() { return [...this.counts.values()].reduce((sum, n) => sum + n, 0); }
    clear() { for (const id of this.counts.keys()) this.counts.set(id, 0); }
    fillSame() {
      this.clear();
      if (this.cap(this.currentId) >= this.size) this.counts.set(this.currentId, this.size);
      else { this.mode = 'mix'; this.querySelector('[data-mode][value="mix"]').checked = true; this.seedMix(); }
    }
    seedMix() { this.clear(); if (this.cap(this.currentId)) this.counts.set(this.currentId, 1); }
    trim() {
      let left = this.size;
      for (const [id, count] of this.counts) { const n = Math.min(count, this.cap(id), left); this.counts.set(id, n); left -= n; }
    }
    change(e) {
      if (this.busy) return;
      if (e.target.matches('[data-size]')) {
        this.size = Number(e.target.value);
        if (!totals.has(this.size)) return;
        if (this.mode === 'same') this.fillSame(); else this.trim();
      } else if (e.target.matches('[data-mode]')) {
        this.mode = e.target.value;
        if (this.mode === 'same') this.fillSame(); else this.seedMix();
      } else return;
      if (!this.uncertain) this.querySelector('[data-message]').hidden = true;
      this.update();
    }
    click(e) {
      const button = e.target.closest('button');
      if (!button || !this.contains(button) || this.busy) return;
      if (button.matches('[data-add]')) { this.add(); return; }
      if (button.matches('[data-reset]')) this.clear();
      if (button.matches('[data-adjust]')) {
        const id = button.closest('[data-soap-id]').dataset.soapId;
        const n = this.counts.get(id) || 0;
        const d = Number(button.dataset.adjust);
        if (d < 0) this.counts.set(id, Math.max(0, n - 1));
        if (d > 0 && this.totalCount() < this.size && n < this.cap(id)) this.counts.set(id, n + 1);
      }
      this.update();
    }
    update() {
      const multi = this.size > 1; const count = this.totalCount();
      this.querySelector('[data-panel]').hidden = !multi;
      this.form?.classList.toggle('mira-native-purchase-hidden', multi);
      this.querySelector('[data-same]').hidden = this.mode !== 'same';
      this.querySelector('[data-mix]').hidden = this.mode !== 'mix';
      this.querySelector('[data-mode][value="same"]').disabled = this.cap(this.currentId) < this.size;
      this.querySelector('[data-same-summary]').textContent = this.size + ' \u00d7 ' + this.byId.get(this.currentId).dataset.title;
      for (const row of this.rows) {
        const id = row.dataset.soapId, n = this.counts.get(id) || 0;
        row.querySelector('[data-count]').textContent = String(n);
        row.querySelector('[data-adjust="-1"]').disabled = this.busy || n <= 0;
        row.querySelector('[data-adjust="1"]').disabled = this.busy || count >= this.size || n >= this.cap(id);
      }
      this.querySelector('[data-progress-text]').textContent = count + ' of ' + this.size + ' bars selected';
      this.querySelector('[data-progress-fill]').style.width = Math.min(100, count / this.size * 100) + '%';
      this.querySelector('[data-total]').textContent = this.money.format((totals.get(this.size) || 0) / 100);
      this.querySelector('[data-saving]').textContent = 'You save ' + this.money.format((discounts.get(this.size) || 0) / 100);
      const add = this.querySelector('[data-add]');
      add.disabled = this.busy || (!this.uncertain && count !== this.size);
      add.textContent = this.uncertain ? 'View your bag' : this.busy ? 'Adding to your bag...' : count === this.size ? 'Add bundle to bag' : 'Choose ' + (this.size - count) + ' more ' + (this.size - count === 1 ? 'bar' : 'bars');
      this.querySelector('.mira-bb').setAttribute('aria-busy', String(this.busy));
      for (const input of this.querySelectorAll('[data-size]')) input.disabled = this.busy;
      this.querySelector('[data-mode][value="mix"]').disabled = this.busy;
      this.querySelector('[data-mode][value="same"]').disabled = this.busy || this.cap(this.currentId) < this.size;
    }
    message(text, lock=false) {
      this.querySelector('[data-message-text]').textContent = text;
      this.querySelector('[data-message]').hidden = false;
      this.uncertain = lock;
    }
    async request(path, options={}) {
      const control = new AbortController(), timer = setTimeout(() => control.abort(), 15000);
      try {
        const response = await fetch(this.root + path, {credentials:'same-origin', cache:'no-store', ...options, headers:{'Accept':'application/json', ...(options.headers || {})}, signal:control.signal});
        const data = await response.json();
        if (!response.ok) { const error = new Error(data.description || data.message || 'The bag could not be updated.'); error.cartResponse = true; throw error; }
        return data;
      } finally {clearTimeout(timer);}
    }
    goToBag() { window.location.assign(this.cartUrl); }
    async add() {
      if (this.uncertain) {this.goToBag(); return;}
      if (this.busy || !discounts.has(this.size) || this.totalCount() !== this.size) return;
      this.busy = true; this.update(); let sent = false;
      try {
        const before = await this.request('cart.js');
        if (before.currency !== 'CAD') throw new Error('Your bag uses a different currency. Please review your bag before choosing this offer.');
        const previous = (before.items || []).filter(i => this.byId.has(String(i.product_id))).reduce((s,i) => s + i.quantity, 0);
        const combined = previous + this.size;
        if (!discounts.has(combined)) throw new Error('Your bag already contains ' + previous + ' eligible bars. Bundle savings apply to 3, 6 or 12 bars together. Please review your bag to adjust the total.');
        const selection = 'mira-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2,8);
        const items = [...this.counts].filter(([,n]) => n > 0).map(([id,n]) => ({id:this.byId.get(id).dataset.variantId, quantity:n, properties:{'_mira_selection':selection,'_mira_bundle_size':String(this.size)}}));
        sent = true;
        await this.request('cart/add.js', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items})});
        const after = await this.request('cart.js');
        const added = (after.items || []).filter(i => i.properties?._mira_selection === selection);
        const addedCount = added.reduce((s,i) => s + i.quantity, 0);
        if (addedCount !== this.size) throw new Error('Some selected bars could not be added. Please review your bag before trying again.');
        const eligible = after.items.filter(i => this.byId.has(String(i.product_id)));
        const actual = eligible.reduce((s,i) => s + i.final_line_price, 0);
        const expected = combined * 900 - discounts.get(combined);
        if (after.currency !== 'CAD' || !Number.isFinite(actual) || Math.abs(actual - expected) > 5) throw new Error('Your bars were added, but the expected bundle saving could not be confirmed. Please review the total in your bag before checkout.');
        this.goToBag();
      } catch(error) {
        this.message(sent ? (error.cartResponse ? error.message + ' Please review your bag before trying again.' : 'Please review your bag before trying again. ' + (error.name === 'AbortError' ? 'The connection timed out while confirming your selection.' : error.message)) : error.message, sent);
      } finally {this.busy = false; this.update();}
    }
  }
  customElements.define('mira-bundle-builder', MiraBundleBuilder);
})();
